import type { ReactNode } from 'react';
import { cn } from '../utils/cn.js';

/**
 * Small row-cell helpers — React ports of KIT.person/org/stack/bar/dl/stats. Called from
 * ListPage.columns[].render() to keep row markup consistent across pages.
 */

export function Person({ name, sub }: { name: string; sub?: ReactNode }) {
  const initials = String(name)
    .replace(/^(Dr|Prof|Mr|Ms)\.\s*/, '')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2);
  return (
    <div className="flex items-center gap-3 min-w-0">
      <span className="avatar avatar-sm">{initials}</span>
      <div className="min-w-0">
        <div className="cell-title truncate">{name}</div>
        {sub ? <div className="cell-sub truncate">{sub}</div> : null}
      </div>
    </div>
  );
}

export function Org({ logo, name, sub }: { logo: ReactNode; name: string; sub?: ReactNode }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <span className="logo-tile">{logo}</span>
      <div className="min-w-0">
        <div className="cell-title truncate">{name}</div>
        {sub ? <div className="cell-sub truncate">{sub}</div> : null}
      </div>
    </div>
  );
}

export function Stack({ title, sub }: { title: ReactNode; sub?: ReactNode }) {
  return (
    <>
      <div className="cell-title">{title}</div>
      {sub ? <div className="cell-sub">{sub}</div> : null}
    </>
  );
}

export function ProgressBar({
  pct, tone, target, min = 110,
}: { pct: number; tone?: 'success' | 'warning' | 'danger' | 'info'; target?: number; min?: number }) {
  return (
    <div className="flex items-center gap-2" style={{ minWidth: min }}>
      <div
        className={cn('bar flex-1', tone && `bar-${tone}`, target != null && 'bar-target')}
        style={target != null ? ({ ['--t' as never]: `${target}%` } as React.CSSProperties) : undefined}
      >
        <span style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className="text-sm w-11 text-right">{pct}%</span>
    </div>
  );
}

export function DL({ pairs }: { pairs: Array<[ReactNode, ReactNode]> }) {
  return (
    <dl className="dl">
      {pairs.map(([k, v], i) => (
        <div key={i}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function StatsRow({ items }: { items: Array<[ReactNode, ReactNode]> }) {
  return (
    <div className={cn('grid gap-2', `grid-cols-${items.length}`)}>
      {items.map(([label, value], i) => (
        <div key={i} className="well p-3">
          <div className="helper">{label}</div>
          <b className="text-lg">{value}</b>
        </div>
      ))}
    </div>
  );
}

export function Badge({ children, tone }: { children: ReactNode; tone?: 'success' | 'warning' | 'danger' | 'info' | 'outline' }) {
  return <span className={cn('badge', tone && `badge-${tone}`)}>{children}</span>;
}
